/**
 * Lymo Price Per Item - Dynamic variant updates
 * Updates the unit price display when variant changes
 */

(function () {
  // Set to false to disable console logs in production
  const DEBUG = false;

  class LymoPricePerItemUpdater {
    constructor() {
      this.init();
    }

    init() {
      // Subscribe to the theme's variantChange event
      // This is fired AFTER the section HTML is replaced by product-info.js
      // and includes the full variant object
      if (
        typeof subscribe !== "undefined" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange.bind(this)
        );
      }

      // Initialize with current variant
      this.initializeFromCurrentVariant();
    }

    disconnectedCallback() {
      // Clean up subscriptions when component is removed
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }
    }

    handleVariantChange(event) {
      // This is called AFTER product-info.js replaces the section HTML
      // The variant object is passed directly in the event data
      if (DEBUG) {
        console.log(
          "lymo-price-per-item: handleVariantChange from PUB_SUB_EVENTS.variantChange",
          event
        );
      }

      const variant = event?.data?.variant;

      if (variant) {
        // Variant object is provided directly from the event
        if (DEBUG) {
          console.log(
            "lymo-price-per-item: variant from event.data.variant",
            variant
          );
        }
        // Use setTimeout to ensure HTML replacement is complete
        setTimeout(() => {
          // Re-query DOM elements since the section HTML was replaced
          const containers = document.querySelectorAll(".lymo-price-per-item");
          this.updatePricePerItem(variant, containers);
        }, 0);
      } else {
        // Fallback: try to get from DOM (shouldn't normally happen)
        if (DEBUG) {
          console.warn(
            "lymo-price-per-item: variant not in event, falling back to DOM query"
          );
        }
        this.initializeFromCurrentVariant();
      }
    }

    initializeFromCurrentVariant() {
      // Get the selected variant data from the DOM
      const variantData = this.findSelectedVariantData();
      const containers = document.querySelectorAll(".lymo-price-per-item");
      if (variantData && containers.length > 0) {
        this.updatePricePerItem(variantData, containers);
      }
    }

    findSelectedVariantData() {
      // Selected variant data is stored in the data-selected-variant attribute
      const variantElement = document.querySelector("[data-selected-variant]");
      if (!variantElement?.textContent) {
        if (DEBUG) {
          console.warn(
            "lymo-price-per-item: Selected variant data not found."
          );
        }
        return null;
      }
      return JSON.parse(variantElement.textContent);
    }

    updatePricePerItem(variant, containers) {
      if (!variant || !containers || containers.length === 0) return;

      containers.forEach((container) => {
        const priceElement = container.querySelector("[data-unit-price]");
        const unitElement = container.querySelector("[data-unit-name]");

        if (!priceElement) return;

        // Check if unit is "item" and variant has unit price
        if (
          variant.unit_price &&
          variant.unit_price_measurement?.reference_unit === "item"
        ) {
          // Update the unit price
          priceElement.innerHTML = this.formatMoney(variant.unit_price);

          // Update the unit name if unitElement exists
          // The unit name should be updated from the new HTML, but if it's not,
          // we'll try to get it from metafields
          if (unitElement) {
            this.updateUnitName(variant, unitElement);
          }

          // Show the container
          container.style.display = "";
        } else {
          // Hide if no unit price or unit is not "item"
          container.style.display = "none";
        }

        // Update variant ID
        container.dataset.variantId = variant.id;
      });
    }

    updateUnitName(variant, unitElement) {
      if (!unitElement || !window.variantStrings?.unit_price_units) {
        if (DEBUG) {
          console.warn(
            "lymo-price-per-item: Unit element or variantStrings not available"
          );
        }
        return;
      }

      // Get the unit name key from variant metafields
      let unitNameKey = "item"; // Default fallback

      // Try to get the metafield value from the variants-metafields script tag
      const variantsMetafieldsElement = document.querySelector(
        "[data-variants-metafields]"
      );
      if (variantsMetafieldsElement?.textContent) {
        try {
          const variantsMetafields = JSON.parse(
            variantsMetafieldsElement.textContent
          );
          const variantMetafields = variantsMetafields.find(
            (v) => v.id === variant.id
          );
          if (
            variantMetafields?.metafields?.special_unit_price_unit_field
          ) {
            unitNameKey = variantMetafields.metafields.special_unit_price_unit_field;
            
            // Handle list type metafield
            // If it's a string that looks like a JSON array, parse it
            if (typeof unitNameKey === "string" && unitNameKey.trim().startsWith("[")) {
              try {
                const parsed = JSON.parse(unitNameKey);
                unitNameKey = Array.isArray(parsed) ? parsed[0] : unitNameKey;
              } catch (e) {
                // If parsing fails, use the string as-is
                if (DEBUG) {
                  console.warn(
                    "lymo-price-per-item: Failed to parse metafield value as JSON:",
                    unitNameKey
                  );
                }
              }
            }
            
            // If it's already an array, get the first value
            if (Array.isArray(unitNameKey)) {
              unitNameKey = unitNameKey[0] || "item";
            }
            
            // Ensure it's a string
            unitNameKey = String(unitNameKey).trim() || "item";
          }
        } catch (e) {
          if (DEBUG) {
            console.warn(
              "lymo-price-per-item: Error parsing variants metafields:",
              e
            );
          }
        }
      }

      // Get the translated unit name from window.variantStrings
      const unitName =
        window.variantStrings.unit_price_units[unitNameKey] ||
        window.variantStrings.unit_price_units.item;

      // Update the unit name element
      unitElement.textContent = unitName;

      if (DEBUG) {
        console.log(
          "lymo-price-per-item: Updated unit name:",
          unitNameKey,
          "->",
          unitName
        );
      }
    }

    formatMoney(cents) {
      // Use Shopify's money format if available
      if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
        return Shopify.formatMoney(
          cents,
          window.theme?.moneyFormat || "{{amount_with_currency}}"
        );
      }

      // Fallback formatting
      const amount = (cents / 100).toFixed(2);
      return `${amount} €`;
    }
  }

  // Initialize on DOMContentLoaded
  if (
    document.readyState === "complete" ||
    document.readyState === "interactive"
  ) {
    new LymoPricePerItemUpdater();
  } else {
    document.addEventListener("DOMContentLoaded", () => {
      new LymoPricePerItemUpdater();
    });
  }

  // Handle Shopify Theme Editor updates or dynamic section loading
  document.addEventListener("shopify:section:load", () => {
    // Re-initialize if the relevant section is loaded/reloaded
    // Check if the elements exist before creating a new instance
    if (document.querySelector(".lymo-price-per-item")) {
      new LymoPricePerItemUpdater();
    }
  });
})();

